import type { ReactNode } from 'react'
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'

interface Props {
  label?: string
  error?: string
  hint?: string
  iconLeft?: ReactNode
  iconRight?: ReactNode
  wrapperClassName?: string
  className?: string
  /** Valor numérico (number | null). null = campo vazio. */
  value: number | null
  /** Callback recebe number | null já parseado. */
  onChange: (next: number | null) => void
  onBlur?: () => void
  /** Casas decimais permitidas (default 0 = inteiro). */
  decimals?: number
  /** Permite valores negativos. Default `false`. */
  allowNegative?: boolean
  /** Min/max para clamp no blur (não impede digitação). */
  min?: number
  max?: number
  placeholder?: string
  disabled?: boolean
  required?: boolean
  name?: string
  id?: string
  /** Mostra X para limpar quando há valor. Default `true`. */
  clearable?: boolean
  /** Prefixo visual (ex.: "R$ "). Aplicado apenas como ornamento. */
  prefix?: string
  /** Sufixo visual (ex.: "%", "kg"). */
  suffix?: string
  /** Separador decimal exibido (default ',' BR). */
  decimalSeparator?: ',' | '.'
}

/**
 * Input numérico que substitui `type="number"` (regra global).
 *
 *  - Aceita campo vazio (sem forçar 0)
 *  - Permite apagar livremente, sem voltar pro min
 *  - Bloqueia digitação de letras (só dígitos + separador decimal)
 *  - Decimais configuráveis via prop `decimals`
 *  - Aplica `min/max` apenas no blur (não impede digitação intermediária)
 *  - Botão X para limpar (regra global)
 *  - Suporte a prefixo/sufixo visual e separador decimal pt-BR (vírgula)
 *
 * O valor exposto via onChange é SEMPRE `number | null` — nunca string.
 * O backend recebe número limpo, sem formatação.
 */
export const NumberInput = forwardRef<HTMLInputElement, Props>(function NumberInput({
  label, error, hint, iconLeft, iconRight, wrapperClassName, className,
  value, onChange, onBlur,
  decimals = 0, allowNegative = false, min, max,
  placeholder, disabled, required, name, id,
  clearable = true, prefix, suffix, decimalSeparator = ',',
}, ref) {
  const hasError = Boolean(error)

  // Mantém uma versão textual interna para suportar entradas como "12," que
  // ainda não são números válidos mas precisam coexistir durante a digitação.
  const formatExternal = (v: number | null): string => {
    if (v === null || v === undefined || Number.isNaN(v)) return ''
    if (decimals > 0) return v.toFixed(decimals).replace('.', decimalSeparator)
    return String(Math.trunc(v))
  }
  const [text, setText] = useState<string>(() => formatExternal(value))
  const editingRef = useRef(false)
  // Sincroniza se o valor externo mudar e o usuário NÃO está editando.
  useEffect(() => {
    if (editingRef.current) return
    setText(formatExternal(value))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, decimals, decimalSeparator])

  // Regex que aceita só dígitos + um único separador decimal (se decimals>0)
  const allowedRegex = useMemo(() => {
    const neg = allowNegative ? '-?' : ''
    if (decimals > 0) {
      const sep = decimalSeparator === ',' ? ',' : '\\.'
      return new RegExp(`^${neg}\\d*(?:${sep}\\d{0,${decimals}})?$`)
    }
    return new RegExp(`^${neg}\\d*$`)
  }, [decimals, decimalSeparator, allowNegative])

  function parse(s: string): number | null {
    if (s === '' || s === '-') return null
    const norm = decimalSeparator === ',' ? s.replace(',', '.') : s
    const n = Number(norm)
    return Number.isNaN(n) ? null : n
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value
    // Substitui ponto por vírgula automaticamente quando separador BR
    const normalized = decimalSeparator === ',' ? raw.replace('.', ',') : raw
    if (normalized !== '' && !allowedRegex.test(normalized)) return
    editingRef.current = true
    setText(normalized)
    onChange(parse(normalized))
  }

  function handleBlur() {
    editingRef.current = false
    const parsed = parse(text)
    let clamped = parsed
    if (parsed !== null) {
      if (typeof min === 'number' && parsed < min) clamped = min
      if (typeof max === 'number' && parsed > max) clamped = max
    }
    if (clamped !== parsed) onChange(clamped)
    setText(formatExternal(clamped))
    onBlur?.()
  }

  function clear() {
    onChange(null)
    setText('')
  }

  const hasValue = text !== ''
  const showClear = clearable && hasValue && !disabled

  return (
    <div className={cn('flex flex-col', wrapperClassName)}>
      {label && (
        <label className="text-xs font-medium text-gray-700 mb-1" htmlFor={id}>
          {label}
          {required && <span className="text-rose-500 ml-0.5">*</span>}
        </label>
      )}
      <div className={cn(
        'relative flex items-center bg-white rounded-md ring-1 transition',
        hasError ? 'ring-rose-300 focus-within:ring-rose-500' : 'ring-gray-200 focus-within:ring-brand-500 focus-within:ring-2',
        disabled && 'opacity-50 bg-gray-50',
      )}>
        {iconLeft && <span className="absolute left-3 text-gray-400 pointer-events-none">{iconLeft}</span>}
        {prefix && <span className={cn('absolute text-xs text-gray-500 select-none pointer-events-none', iconLeft ? 'left-9' : 'left-3')}>{prefix}</span>}
        <input
          ref={ref}
          id={id}
          name={name}
          type="text"
          inputMode={decimals > 0 ? 'decimal' : 'numeric'}
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          className={cn(
            'w-full bg-transparent rounded-md text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none',
            'px-3 py-2',
            iconLeft  && 'pl-9',
            prefix    && (iconLeft ? 'pl-[3.25rem]' : 'pl-10'),
            (iconRight || showClear || suffix) && 'pr-9',
            className,
          )}
        />
        {suffix && !showClear && <span className="absolute right-3 text-xs text-gray-500 pointer-events-none">{suffix}</span>}
        {showClear ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Limpar"
            onClick={clear}
            className="absolute right-2 cursor-pointer inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          >
            <X size={14} />
          </button>
        ) : iconRight && !suffix ? (
          <span className="absolute right-3 text-gray-400 pointer-events-none">{iconRight}</span>
        ) : null}
      </div>
      {(error || hint) && (
        <span className={cn('text-[11px] mt-1', hasError ? 'text-rose-600' : 'text-gray-500')}>
          {error || hint}
        </span>
      )}
    </div>
  )
})
